'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { validateUsername as validateUsernameShared } from '@/shared';
import type { ApiSuccessResponse, AuthResponseData } from '@/shared';
import { API, ENV } from '../../config/env';
import { persistAuth } from '../../utils/storage';
import { api, ApiError } from '@/lib/api';

interface ValidationErrors {
  name?: string;
  username?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  otp?: string;
}

export default function SignupPage() {
  const [step, setStep] = useState<'signup' | 'verify'>('signup');
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return 'Full name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    if (name.trim().length > 50) return 'Name must be less than 50 characters';
    return undefined;
  };

  const validateUsername = (username: string): string | undefined => {
    const result = validateUsernameShared(username);
    return result.isValid ? undefined : result.error;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (password.length > 128) return 'Password must be less than 128 characters';
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
      return 'Password must include uppercase, lowercase, and a number';
    }
    return undefined;
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): string | undefined => {
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== password) return 'Passwords do not match';
    return undefined;
  };

  const validateOTP = (value: string): string | undefined => {
    if (!value) return 'OTP is required';
    if (!/^\d{6}$/.test(value)) return 'OTP must be 6 digits';
    return undefined;
  };

  const validateField = (field: string, value: string) => {
    let nextError: string | undefined;

    switch (field) {
      case 'name':
        nextError = validateName(value);
        break;
      case 'username':
        nextError = validateUsername(value);
        break;
      case 'email':
        nextError = validateEmail(value);
        break;
      case 'password':
        nextError = validatePassword(value);
        break;
      case 'confirmPassword':
        nextError = validateConfirmPassword(value, formData.password);
        break;
      case 'otp':
        nextError = validateOTP(value);
        break;
      default:
        nextError = undefined;
    }

    setValidationErrors((prev) => ({ ...prev, [field]: nextError }));
  };

  const handleBlur = (field: string, value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'otp') {
      setOtp(value.replace(/\D/g, '').slice(0, 6));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (error) setError('');
    if (touched[name]) validateField(name, value);
  };

  const validateForm = (): boolean => {
    if (step === 'signup') {
      const nameError = validateName(formData.name);
      const usernameError = validateUsername(formData.username);
      const emailError = validateEmail(formData.email);
      const passwordError = validatePassword(formData.password);
      const confirmPasswordError = validateConfirmPassword(formData.confirmPassword, formData.password);

      setValidationErrors({
        name: nameError,
        username: usernameError,
        email: emailError,
        password: passwordError,
        confirmPassword: confirmPasswordError,
      });
      setTouched({
        name: true,
        username: true,
        email: true,
        password: true,
        confirmPassword: true,
      });

      return !nameError && !usernameError && !emailError && !passwordError && !confirmPasswordError;
    }

    const otpError = validateOTP(otp);
    setValidationErrors({ otp: otpError });
    setTouched((prev) => ({ ...prev, otp: true }));
    return !otpError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    if (step === 'signup') {
      try {
        const result = await api.post<ApiSuccessResponse<{ message: string }>>(API.SIGNUP_REQUEST, {
          email: formData.email,
          username: formData.username,
          name: formData.name,
          password: formData.password,
        });
        setSuccess(result.data?.message || 'Verification code sent to your email.');
        setStep('verify');
      } catch (err) {
        if (err instanceof ApiError) setError(err.message);
        else setError('Hit a snag. Give it another shot.');
      }
    } else {
      try {
        const result = await api.post<ApiSuccessResponse<AuthResponseData>>(API.SIGNUP_VERIFY, {
          email: formData.email,
          otp,
        });
        persistAuth(result.data.token, result.data.refreshToken, result.data);
        window.location.href = '/dashboard';
      } catch (err) {
        if (err instanceof ApiError) setError(err.message);
        else setError('Hit a snag. Give it another shot.');
      }
    }

    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.post<ApiSuccessResponse<AuthResponseData>>(API.GOOGLE_AUTH, {
        idToken: credentialResponse.credential,
      });
      persistAuth(result.data.token, result.data.refreshToken, result.data);
      window.location.href = result.data.usernameRequired ? '/onboarding' : '/dashboard';
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-12 sm:px-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-7 text-center">
          <Link href="/" className="inline-flex items-center gap-3">
            <Image src="/logo-icon.svg" alt="DiecastShowcase logo" width={44} height={44} className="h-11 w-11" priority />
            <span className="text-2xl font-extrabold tracking-tight text-foreground">DiecastShowcase</span>
          </Link>
          <h1 className="mt-6 text-3xl font-bold text-foreground">
            {step === 'signup' ? 'Create your collector account' : 'Verify your email'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {step === 'signup'
              ? 'Tell us what you collect. We will take it from there.'
              : `Enter the 6-digit code sent to ${formData.email}.`}
          </p>
          <div className="mx-auto mt-4 flex max-w-[220px] items-center gap-2">
            <span className={`h-1 flex-1 rounded-full ${step === 'signup' ? 'bg-primary' : 'bg-primary/35'}`} />
            <span className={`h-1 flex-1 rounded-full ${step === 'verify' ? 'bg-primary' : 'bg-primary/35'}`} />
          </div>
        </div>

        <div className="card-shell p-6 sm:p-7">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && <div className="rounded-lg border border-destructive/35 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
            {success && <div className="rounded-lg border border-primary/35 bg-primary/10 px-4 py-3 text-sm text-primary">{success}</div>}

            {step === 'signup' ? (
              <>
                <div>
                  <label htmlFor="name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Full name</label>
                  <input id="name" name="name" value={formData.name} onChange={handleChange} onBlur={(e) => handleBlur('name', e.target.value)} className={`form-input ${validationErrors.name && touched.name ? 'border-destructive focus:ring-destructive' : ''}`} />
                  {validationErrors.name && touched.name && <p className="mt-1 text-sm text-destructive">{validationErrors.name}</p>}
                </div>
                <div>
                  <label htmlFor="username" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Username</label>
                  <input id="username" name="username" value={formData.username} onChange={handleChange} onBlur={(e) => handleBlur('username', e.target.value)} className={`form-input ${validationErrors.username && touched.username ? 'border-destructive focus:ring-destructive' : ''}`} />
                  <p className="mt-1 text-xs text-muted-foreground">Profile URL: diecastshowcase.com/{formData.username || 'yourname'}</p>
                  {validationErrors.username && touched.username && <p className="mt-1 text-sm text-destructive">{validationErrors.username}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Email</label>
                  <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} onBlur={(e) => handleBlur('email', e.target.value)} className={`form-input ${validationErrors.email && touched.email ? 'border-destructive focus:ring-destructive' : ''}`} />
                  {validationErrors.email && touched.email && <p className="mt-1 text-sm text-destructive">{validationErrors.email}</p>}
                </div>
                <div>
                  <label htmlFor="password" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Password</label>
                  <input id="password" name="password" type="password" value={formData.password} onChange={handleChange} onBlur={(e) => handleBlur('password', e.target.value)} className={`form-input ${validationErrors.password && touched.password ? 'border-destructive focus:ring-destructive' : ''}`} />
                  {validationErrors.password && touched.password && <p className="mt-1 text-sm text-destructive">{validationErrors.password}</p>}
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Confirm password</label>
                  <input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} onBlur={(e) => handleBlur('confirmPassword', e.target.value)} className={`form-input ${validationErrors.confirmPassword && touched.confirmPassword ? 'border-destructive focus:ring-destructive' : ''}`} />
                  {validationErrors.confirmPassword && touched.confirmPassword && <p className="mt-1 text-sm text-destructive">{validationErrors.confirmPassword}</p>}
                </div>
              </>
            ) : (
              <div>
                <label htmlFor="otp" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">6-digit code</label>
                <input id="otp" name="otp" value={otp} onChange={handleChange} onBlur={(e) => handleBlur('otp', e.target.value)} className={`form-input text-center font-mono tracking-[0.25em] ${validationErrors.otp && touched.otp ? 'border-destructive focus:ring-destructive' : ''}`} />
                {validationErrors.otp && touched.otp && <p className="mt-1 text-sm text-destructive">{validationErrors.otp}</p>}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
              {loading ? (step === 'signup' ? 'Sending code...' : 'Verifying...') : (step === 'signup' ? 'Send OTP' : 'Verify and create account')}
            </button>
          </form>

          {step === 'verify' && (
            <button type="button" onClick={() => setStep('signup')} className="mt-4 w-full text-sm font-medium text-muted-foreground">
              Back to signup
            </button>
          )}

          {step === 'signup' && ENV.GOOGLE_CLIENT_ID && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  <span className="bg-card px-2">Or continue with</span>
                </div>
              </div>
              <div className="mt-4 flex justify-center">
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in failed. Please try again.')} width="100%" />
              </div>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-secondary-foreground">
            Already have an account?{' '}
            <Link href="/login" className="font-semibold text-primary">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

